import { GroupCreateAPI } from '../api/group.model';
import GroupsCalls from '../api/GroupCalls';

import { AppDispatch, AppStore } from '../../store.model';
import { postUsersRightsByEmail } from '../../users/actions';

import { IconUpload } from '../groups.model';
import { UserService } from '../../users/api/UserService';
import fetchOwnRights from '../../rights/actions';
import { getMyUserId } from '../../auth/selectors';
import { GroupAPI } from '../api/GroupAPI';
import { getAllUsers } from '../../users/selectors';
import RightAPI from '../../rights/api/RightAPI';

/**
 * Post group: call the backend to add a new group
 * @param object groupData
 * @param object iconUpload
 * @param string parentGroupID
 */
export const postGroup =
  (
    groupData: GroupCreateAPI,
    iconUpload: IconUpload | undefined,
    parentGroupID?: string
  ) =>
  async (dispatch: AppDispatch, getState: AppStore['getState']) => {
    const groupsCalls = new GroupsCalls();
    const users = [getMyUserId(getState())];
    await groupsCalls.postGroup(groupData, users, iconUpload);
    await dispatch(fetchOwnRights());

    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
  };

/**
 * Put group: call the backend to update a group
 * @param string groupId
 * @param object groupData
 * @param object iconUpload
 */
export const putGroup =
  (
    groupId: string,
    groupData: Partial<GroupCreateAPI>,
    iconUpload?: IconUpload
  ) =>
  async (dispatch: AppDispatch) => {
    // only if you are lazy and needs as thunk, otherwise use the api directly as useMutation
    const groupResponse = await dispatch(
      GroupAPI.endpoints.putGroup.initiate({
        groupId,
        groupData,
        iconUpload,
      })
    ).unwrap();

    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
    return groupResponse;
  };

/**
 * Put group users with roles: call the backend to update a group adding a users with roles
 */
export const putGroupUsersWithRoles =
  (groupId: string, usersIds: string[], rolesIds: string[]) =>
  async (dispatch: AppDispatch) => {
    const userService = new UserService();
    await userService.putRolesGroupUsers(usersIds, rolesIds, groupId);
    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
  };
/**
 * Put group users: call the backend to update a group adding a users
 * @param string groupId
 * @param array usersId
 */
export const putGroupUsers =
  (groupId: string, usersIds: string[]) => async (dispatch: AppDispatch) => {
    const groupsCalls = new GroupsCalls();
    await groupsCalls.putGroupUsers(groupId, usersIds);
    await dispatch(
      RightAPI.util.invalidateTags([{ type: 'rights-user', id: groupId }])
    );
    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
  };

/**
 * Put group users: call the backend to update a group adding a users
 * @param string groupId
 * @param array emails
 */
export const putGroupUsersByEmail =
  (groupId: string, emails: string[]) => async (dispatch: AppDispatch) => {
    const groupsCalls = new GroupsCalls();
    await groupsCalls.putGroupUsers(groupId, emails);
    const usersIds = await groupsCalls.getUsersByGroupId(groupId);
    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
  };

export const assignUsersToGroup =
  (groupId: string, usersEmails: string[], newRolesIdList: string[]) =>
  async (dispatch: AppDispatch, getState: AppStore['getState']) => {
    const users = getAllUsers(getState());
    /* filter users via email */
    const usersByEmail = usersEmails.filter(
      (email) => !users.find((user) => user.email === email)
    );
    /* filter users via id */
    const usersFoundIds = users
      .filter((user) => usersEmails.includes(user.email))
      .map((user) => user.id);

    /* Assign users with id */
    if (usersFoundIds.length > 0) {
      if (newRolesIdList && newRolesIdList.length > 0) {
        await dispatch(
          putGroupUsersWithRoles(groupId, usersFoundIds, newRolesIdList)
        );
      } else {
        await dispatch(putGroupUsers(groupId, usersFoundIds));
      }
    }

    /* Assign users via email */
    if (usersByEmail.length > 0) {
      await dispatch(putGroupUsersByEmail(groupId, usersByEmail));

      if (newRolesIdList && newRolesIdList.length > 0) {
        await dispatch(
          postUsersRightsByEmail(usersByEmail, newRolesIdList, groupId)
        );
      }
    }
    await dispatch(
      RightAPI.util.invalidateTags([{ type: 'rights-user', id: groupId }])
    );
  };
/**
 * Delete group: call the backend to delete a  group
 * @param string groupId
 */
export const deleteGroup =
  (groupId: string) => async (dispatch: AppDispatch) => {
    const groupsCalls = new GroupsCalls();
    await groupsCalls.deleteGroup(groupId);
    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
  };

/**
 * Delete group icon : call the backend to delete group's icon
 * @param string groupId
 */
export const deleteGroupIcon =
  (groupId: string) => async (dispatch: AppDispatch) => {
    const groupsCalls = new GroupsCalls();
    await groupsCalls.deleteGroupIcon(groupId);
    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
  };

/**
 * Delete group user : call the backend to delete group's user
 * @param string groupId
 * @param string userId
 */
export const deleteGroupUser =
  (groupId: string, userId: string) => async (dispatch: AppDispatch) => {
    const groupsCalls = new GroupsCalls();

    await groupsCalls.deleteGroupUser(groupId, userId);
    await dispatch(
      GroupAPI.endpoints.getGroups.initiate(undefined, {
        forceRefetch: true,
      })
    ).unwrap();
  };

/**
 * Delete group device : call the backend to delete group's device
 * @param string groupId
 * @param string deviceId
 */
export const deleteGroupDevice =
  (groupId: string, deviceId: string) => async (dispatch: AppDispatch) => {
    return await dispatch(
      GroupAPI.endpoints.deleteDeviceGroup.initiate(
        {
          groupId,
          commanderId: deviceId,
        },
        {
          fixedCacheKey: `deleteDeviceGroup-${groupId}-${deviceId}`,
        }
      )
    ).unwrap();
  };
